import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import algoliasearch from 'algoliasearch/lite';
import i18n from 'i18n-react';
import { Configure, Hits, Index, InstantSearch, Pagination, SearchBox } from 'react-instantsearch';

import Modal from 'components/Modal';
import Hit from 'components/SearchModal/Hit';
import NoResults from 'components/SearchModal/NoResults';

import { useSettings } from 'hooks/useSettings';

import { promoteSearchIds } from "settings";

// Loading component to prevent flickering
const LoadingIndicator = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    padding: '2rem',
    color: '#666'
  }}>
    {i18n.translate('searchModal.loading') || 'Loading...'}
  </div>
);

/** Object to map language based columns to the proper column names in the algolia index. */
const pagesMultilingualKeys = {
  en: {
    title: 'title',
    clean_text: 'clean_text',
  },
  de: {
    title: 'title_de-DE',
    clean_text: 'clean_text_de-DE',
  },
  es: {
    title: 'title_es-41',
    clean_text: 'clean_text_es-41',
  },
  fr: {
    title: 'title_fr-FR',
    clean_text: 'clean_text_fr-FR',
  },
  it: {
    title: 'title_it-IT',
    clean_text: 'clean_text_it-IT',
  },
  ko: {
    title: 'title_ko-KR',
    clean_text: 'clean_text_ko-KR',
  },
  pt: {
    title: 'title_pt-BR',
    clean_text: 'clean_text_pt-BR',
  },
};

// Higher order component to bind the onSelect function and the proper type to the Hit component. Required to prevent: no-unstable-nested-components eslint (which prevents unnecessary renders).
const getPreparedHitComponent =
  (onSelect, type, langFile, user = {}) =>
  ({ hit }) => {
    let translationKeys = pagesMultilingualKeys.en;
    if (type === 'pages' && langFile) {
      translationKeys = pagesMultilingualKeys[langFile] || pagesMultilingualKeys.en;
    }
    return <Hit 
      hit={hit}
      type={type} 
      user={user} 
      onSelect={onSelect} 
      pagesTranslationKeys={translationKeys} 
      langFile={langFile || 'en'}
    />;
  };

/**
 * @typedef {object} IndexSearchProps
 * @property {string} algoliaId An algolia search id. See algolia dashboard.
 * @property {string} algoliaSearchKey An algolia search key. See algolia dashboard.
 * @property {Function} onHide A function to call when the modal is closed.
 * @property {string} [defaultQuery=''] The default search query. Defaults to an empty string. Default is `''`
 * @property {Function} [onQuery=null] A function to call when the search query is updated. Gets passed the current
 *   query. Default is `null`
 * @property {boolean} [pages=false] Whether to search pages. Defaults to false. Default is `false`
 * @property {boolean} [salesCompanion=false] Whether to search sales companion items. Defaults to false. Default is `false`
 * @property {boolean} [isVisible=false] Whether the search modal is visible. Defaults to false.
 */

/**
 * A specific implementation/configuration that allows for searching a desired subapp.
 *
 * @param {IndexSearchProps} props Props
 * @returns {JSX.Element} A search component, specifically configured for searching within specific tests.
 */
const IndexSearch = ({
  algoliaId,
  algoliaSearchKey,
  hitsPerPage = 8,
  attributesToSnippet: attributesToSnippetProp = ['clean_text', 'title'],
  onHide,
  defaultQuery = '',
  onQuery = null,
  pages = false,
  exhibitionTiles = false,
  salesCompanion = false,
  user = {},
  isVisible = false,
}) => {
  
  const searchClient = useMemo(() => algoliasearch(algoliaId, algoliaSearchKey), [algoliaId, algoliaSearchKey]);
  const indexName = 'pages';
  const { appId, lang: { langFile }, loaded: settingsLoaded } = useSettings();

  //  let partnerAppIDs = [];
  // if (subapps) {
  //   partnerAppIDs = subapps.map(({modules}) => {
  //     // Handle multiple levels of nesting in modules
  //     const extractSessionAppIds = (moduleArray) => {
  //       const appIds = [];
  //       moduleArray.forEach(module => {
  //         if (module.sessionAppId) {
  //           appIds.push(module.sessionAppId);
  //         }
  //         if (module.modules && Array.isArray(module.modules)) {
  //           appIds.push(...extractSessionAppIds(module.modules));
  //         }
  //       });
  //       return appIds;
  //     };
  //     return extractSessionAppIds(modules);
  //   }).flat();
  // }

  // configures params based on langFile
  const { titleColumn, cleanTextColumn, attributesToSnippet } = useMemo(() => {
    // Ensure we have a valid language file, fallback to 'en' if undefined
    const currentLang = langFile && pagesMultilingualKeys[langFile] ? langFile : 'en';
    const langKeys = pagesMultilingualKeys[currentLang];
    
    const snippetAttributes = attributesToSnippetProp.map(
      (attribute) => langKeys[attribute] || attribute,
    );

    return {
      titleColumn: langKeys.title,
      cleanTextColumn: langKeys.clean_text,
      attributesToSnippet: snippetAttributes,
    };
  }, [attributesToSnippetProp, langFile]);

  const constructFilters = useMemo(() => {
    let filters = `app_id = ${appId}`;
    filters += ' AND archived = 0';
    filters += ' AND type_id != 5';
    filters += ' AND NOT title:hidden';
    filters += ' AND graduated_unlock_only != 1';
    return filters;
  }, [appId]);

  const constructFiltersSC = useMemo(() => {
    const filterAppId = promoteSearchIds[langFile] || promoteSearchIds.default || appId;
    let filters = `app_id = ${filterAppId}`;
    filters += ' AND graduated_unlock_only != 1';
    return filters;
  }, [langFile, appId]);

   const constructFiltersPartner = useMemo(() => {
    // if (partnerAppIDs.length === 0) {
    //   return 'app_id = -1'; // Return a filter that will never match anything
    // }
    let filters = `parent_app_id  = 1441`;
    filters += ' AND archived = 0';
    filters += ' AND type_id != 5';
    filters += ' AND NOT title:hidden';
    filters += ' AND graduated_unlock_only != 1';
    return filters;
  }, [appId]);

  const constructFiltersExhibitionTiles = useMemo(() => {
     const filters = `app_id = ${appId}`;
        return filters;
  }, [exhibitionTiles, appId]);

  const queryHook = useMemo(() => (searchQuery, search) => {
    if (onQuery) onQuery(searchQuery);
    search(searchQuery);
  }, [onQuery]);

  const PreparedSalesCompanionHit = useMemo(() => getPreparedHitComponent(onHide, 'sales_companion_items'), [onHide]);

  const PreparedPagesHit = useMemo(() => getPreparedHitComponent(onHide, indexName, langFile), [langFile, onHide]);
  
  const PreparedPartnerPagesHit = useMemo(
    () => getPreparedHitComponent(onHide, 'partner_pages', langFile, user), 
    [langFile, onHide, user]
  );

  const PreparedExhibitionTilesHit = useMemo(() => getPreparedHitComponent(undefined, 'exhibition_tiles', langFile, user), [langFile, onHide, user]);

  // Prevent search from reinitializing by using a stable key
  const searchKey = useMemo(() => `search-${appId}-${langFile}`, [appId, langFile]);
  
  // Memoize the search container to prevent flickering
  const searchContainer = useMemo(() => {
    if (!settingsLoaded || !langFile) return <LoadingIndicator />;
    
    return (
      <div className="search__container">
        <Index key={`pages-${appId}-${langFile}`} indexName="pages">
          <h3>{i18n.translate('searchModal.pages')}</h3>
          <Configure
              filters={constructFilters}
              hitsPerPage={hitsPerPage}
              attributesToSnippet={attributesToSnippet}
              restrictSearchableAttributes={[titleColumn, cleanTextColumn]}
            />
          <NoResults>
            <Hits key={`pages-hits-${appId}-${langFile}`} hitComponent={PreparedPagesHit} />
          </NoResults>
          <Pagination />
        </Index>
        <Index key={`partner-pages-${appId}-${langFile}`} indexName="pages">
          <h3>{i18n.translate('searchModal.partnerPages')}</h3>
          <Configure
              filters={constructFiltersPartner}
              hitsPerPage={hitsPerPage}
              attributesToSnippet={attributesToSnippet}
              restrictSearchableAttributes={[
                titleColumn, 
                cleanTextColumn
              ]}
            />
          <NoResults>
            <Hits key={`partner-pages-hits-${appId}-${langFile}`} hitComponent={PreparedPartnerPagesHit} />
          </NoResults>
          <Pagination />
        </Index>
        
        <Index key={`sales-companion-${appId}-${langFile}`} indexName="sales_companion_items">
          <h3>{i18n.translate('searchModal.salesCompanion')}</h3>
          <Configure
              filters={constructFiltersSC}
              hitsPerPage={hitsPerPage}
              attributesToSnippet={attributesToSnippetProp}
            />
          <NoResults>
            <Hits key={`sales-companion-hits-${appId}-${langFile}`} hitComponent={PreparedSalesCompanionHit} />
          </NoResults>
          <Pagination />
        </Index>
        <Index key={`exhibition-tiles-${appId}-${langFile}`} indexName="exhibition_tiles">
          <h3>{i18n.translate('searchModal.exhibitionTiles')}</h3>
          <Configure
              filters={constructFiltersExhibitionTiles}
              hitsPerPage={hitsPerPage}
              attributesToSnippet={attributesToSnippetProp}
            />
          <NoResults>
            <Hits key={`exhibition-tiles-hits-${appId}-${langFile}`} hitComponent={PreparedExhibitionTilesHit} />
          </NoResults>
          <Pagination />
        </Index>
      </div>
    );
  }, [
    settingsLoaded,
    appId,
    langFile,
    pages,
    salesCompanion,
    constructFilters,
    constructFiltersPartner,
    constructFiltersSC,
    hitsPerPage,
    attributesToSnippet,
    attributesToSnippetProp,
    titleColumn,
    cleanTextColumn,
    exhibitionTiles,
    constructFiltersExhibitionTiles,
    PreparedPagesHit,
    PreparedPartnerPagesHit,
    PreparedSalesCompanionHit,
    PreparedExhibitionTilesHit,
  ]);

  return (
    <Modal id="search-modal__index" show={isVisible} onHide={onHide} bgClose>
      <div className="search-modal">
        <InstantSearch
          key={searchKey}
          searchClient={searchClient}
          indexName={indexName}
          initialUiState={{
            pages: {
              query: defaultQuery,
            },
            sales_companion_items: {
              query: defaultQuery,
            },
          }}
          future={{
            preserveSharedStateOnUnmount: true
          }}
        >
          <SearchBox 
            autoFocus 
            placeholder={i18n.translate('nav.search')} 
            queryHook={queryHook}
            style={{
              '--ais-SearchBox-input-padding': '1rem 1.5rem',
              '--ais-SearchBox-input-font-size': '1.2rem',
              '--ais-SearchBox-input-border': '2px solid #ddd',
              '--ais-SearchBox-input-border-radius': '8px',
              '--ais-SearchBox-input-box-shadow': '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
          {searchContainer}
        </InstantSearch>
      </div>
    </Modal>
  );
};

IndexSearch.propTypes = {
  algoliaId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  hitsPerPage: PropTypes.number,
  attributesToSnippet: PropTypes.arrayOf(PropTypes.string),
  defaultQuery: PropTypes.string,
  onQuery: PropTypes.func,
  onHide: PropTypes.func.isRequired,
  pages: PropTypes.bool,
  exhibitionTiles: PropTypes.bool,
  salesCompanion: PropTypes.bool,
  isVisible: PropTypes.bool,
  user: PropTypes.shape({}),
};

export default React.memo(IndexSearch);
